import React from 'react';
import {useIntl} from 'react-intl';
import Styled from 'styled-components';
import {Button, Detail, EMPHASIS, FlexRow, Text, tokensForThemeProvider} from '@forecast-it/design-system';
import {formatDataLine} from './insertTemplateFormat';

export type InsertableTemplate = {
	readonly id: string;
	readonly name: string | null;
	readonly description: string | null;
	readonly phaseCount: number | null;
	readonly totalMinutes: number | null;
	readonly roleCount: number | null;
	readonly durationDays: number | null;
	readonly includeDeliverables: boolean | null;
	readonly deliverableCount: number | null;
};

const Row = Styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	padding: 14px 16px;
	border-bottom: 1px solid ${tokensForThemeProvider.sky.sys.color.border.decorative};

	&:last-child {
		border-bottom: none;
	}
`;

// The details take all the width the buttons leave, and shrink first, so the Preview and Insert buttons
// sit at the right edge and always stay whole beside a long name. A name with no spaces breaks anywhere
// rather than running under the buttons.
const Details = Styled.div`
	display: flex;
	flex-direction: column;
	gap: 3px;
	min-width: 0;
	flex: 1;
	overflow-wrap: anywhere;
`;

type Props = {
	template: InsertableTemplate;
	projectUsesDeliverables: boolean;
	onPreview: (template: InsertableTemplate) => void;
	onInsert: (template: InsertableTemplate) => void;
};

export const InsertTemplateListItem = ({template, projectUsesDeliverables, onPreview, onInsert}: Props): JSX.Element => {
	const intl = useIntl();
	return (
		<Row data-cy={`insert-baseline-template-row-${template.id}`}>
			<Details>
				<Text size="1">{template.name}</Text>
				{template.description ? (
					<Text color="auxiliary" size="2">
						{template.description}
					</Text>
				) : null}
				<Detail color="auxiliary">{formatDataLine(intl, template, projectUsesDeliverables)}</Detail>
			</Details>
			<FlexRow gap="s" flex="0 0 auto">
				<Button
					emphasis={EMPHASIS.LOW}
					onPress={() => onPreview(template)}
					data-cy={`insert-baseline-template-preview-${template.id}`}
				>
					{intl.formatMessage({id: 'baseline_templates.insert.preview'})}
				</Button>
				<Button
					emphasis={EMPHASIS.MEDIUM}
					onPress={() => onInsert(template)}
					data-cy={`insert-baseline-template-insert-${template.id}`}
				>
					{intl.formatMessage({id: 'baseline_templates.insert.insert'})}
				</Button>
			</FlexRow>
		</Row>
	);
};
